/**
 * Centralized React Query configuration constants
 * This ensures consistent caching and retry behavior across the application
 */

const MINUTE = 60 * 1000;

export const QUERY_CONFIG = {
    // Default stale time - how long data is considered fresh (5 minutes)
    DEFAULT_STALE_TIME: 5 * MINUTE,

    // Default garbage collection time - how long data stays in cache (10 minutes)
    DEFAULT_GC_TIME: 10 * MINUTE,

    // Default retry count
    DEFAULT_RETRY: 3,

    // Long-lived data (countries, projects, etc.) - cache for 15 minutes
    LONG_LIVED_STALE_TIME: 15 * MINUTE,
    LONG_LIVED_GC_TIME: 30 * MINUTE,

    // Short-lived data (user lists, etc.) - cache for 1 minute
    SHORT_LIVED_STALE_TIME: MINUTE,
    SHORT_LIVED_GC_TIME: 5 * MINUTE,

    // Real-time data - no caching
    REAL_TIME_STALE_TIME: 0,
    REAL_TIME_GC_TIME: 0,
} as const;

/**
 * Common query options for different data types
 */
export const QUERY_OPTIONS = {
    // For static reference data (countries, projects, etc.)
    REFERENCE_DATA: {
        staleTime: QUERY_CONFIG.LONG_LIVED_STALE_TIME,
        gcTime: QUERY_CONFIG.LONG_LIVED_GC_TIME,
        refetchOnWindowFocus: false,
        retry: QUERY_CONFIG.DEFAULT_RETRY,
    },

    // For user-generated content (lists, forms, etc.)
    USER_DATA: {
        staleTime: QUERY_CONFIG.SHORT_LIVED_STALE_TIME,
        gcTime: QUERY_CONFIG.SHORT_LIVED_GC_TIME,
        refetchOnWindowFocus: false,
        retry: QUERY_CONFIG.DEFAULT_RETRY,
    },

    // For real-time data that needs frequent updates
    REAL_TIME: {
        staleTime: QUERY_CONFIG.REAL_TIME_STALE_TIME,
        gcTime: QUERY_CONFIG.REAL_TIME_GC_TIME,
        refetchOnWindowFocus: true,
        retry: 1,
    },

    // Default options
    DEFAULT: {
        staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME,
        gcTime: QUERY_CONFIG.DEFAULT_GC_TIME,
        refetchOnWindowFocus: false,
        retry: QUERY_CONFIG.DEFAULT_RETRY,
    },
} as const;
